import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api';
import { useSession } from '../hooks/useSession';
import { relativeTime, shortAddress } from '../lib/format';
import { EscrowMeter, meterState } from '../components/EscrowMeter';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'paid', label: 'Paid' },
  { key: 'awaiting-reply', label: 'Owed a reply' },
] as const;

export function Inbox() {
  const { profile } = useSession();
  const [filter, setFilter] = useState<string>('all');
  const { data, isLoading } = useQuery({
    queryKey: ['threads', filter],
    queryFn: () => api.threads(filter),
  });

  const items = data?.items ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <h2>Inbox</h2>
        <div className="spacer" />
        <div className="filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className="filter"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* A priced inbox with no channel linked means paid messages can expire
          unseen, so this stays until they link one. */}
      {profile && !profile.reachable && (
        <div className="nudge">
          <p>
            Nothing tells you when a message arrives. Link Telegram or Discord and we'll DM
            you — senders never see which, or whether you did.
          </p>
          <div className="spacer" />
          <Link to="/settings" className="btn btn-sm">Set that up</Link>
        </div>
      )}

      {isLoading && <div className="empty">Loading…</div>}

      {!isLoading && items.length === 0 && (
        <div className="empty">
          <p style={{ marginTop: 0, color: 'var(--bone-2)' }}>
            {filter === 'all' ? 'No messages yet.' : 'Nothing matches this filter.'}
          </p>
          <Link to="/compose" className="btn btn-primary">
            Write to a wallet
          </Link>
        </div>
      )}

      <div className="threads">
        {items.map((t) => {
          // Only a live escrow the viewer owes a reply on colours the row.
          const live = t.escrow?.status === 'PENDING' && !t.escrow.repliedAt ? t.escrow : null;
          const urgency = live ? meterState(live.fundedAt, live.deadlineAt) : null;
          const owed = t.awaitingMyReply && live;
          const rowClass = owed
            ? urgency?.urgent
              ? 'thread-row is-urgent'
              : 'thread-row is-owed'
            : 'thread-row';

          return (
            <Link key={t.id} to={`/thread/${t.id}`} className={rowClass}>
              <div className="thread-id">
                {t.unreadCount > 0 && <span className="unread-dot" aria-label="unread" />}
                <span className="name">
                  {t.counterpartyDisplayName ?? shortAddress(t.counterpartyAddress, 6, 6)}
                </span>
                {t.counterpartyDisplayName && (
                  <span className="addr">{shortAddress(t.counterpartyAddress)}</span>
                )}
                <span className="addr">{relativeTime(t.lastMessageAt)}</span>
              </div>

              <div className="thread-meter">
                {t.escrow ? (
                  <EscrowMeter escrow={t.escrow} />
                ) : (
                  <span className="meter-free">no fee</span>
                )}
              </div>

              <div className="thread-preview">{t.lastMessagePreview}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
